/**
 * useFocusTrap — a tiny, self-contained focus trap for modal/dialog overlays.
 *
 * Intentionally lightweight (no UI-library / `@floating-ui` DOM wrappers) so it
 * can be layered onto EXISTING markup without changing the rendered structure,
 * classes, or visuals. It only:
 *   - moves focus to a preferred element (or the first tabbable) when activated,
 *   - keeps Tab / Shift+Tab cycling within the container,
 *   - optionally invokes `onEscape` when Escape is pressed,
 *   - restores focus to the previously-focused element on deactivation.
 *
 * Usage:
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   useFocusTrap(containerRef, isOpen, { onEscape, initialFocusRef });
 *   <div ref={containerRef} role="dialog" aria-modal="true"> ... </div>
 */

import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface FocusTrapOptions {
  /** Called when Escape is pressed while the trap is active. */
  onEscape?: () => void;
  /** Element to focus first when the trap activates. Falls back to first tabbable. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

function getTabbable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
}

/**
 * Traps focus within `containerRef` while `active` is true.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  options: FocusTrapOptions = {},
): void {
  const { onEscape, initialFocusRef } = options;

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Remember what was focused so we can restore it on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the dialog (preferred element, else first tabbable, else container).
    const focusInitial = (): void => {
      const preferred = initialFocusRef?.current;
      if (preferred) {
        preferred.focus();
        return;
      }
      const tabbables = getTabbable(container);
      if (tabbables.length > 0) {
        tabbables[0].focus();
      } else {
        container.focus();
      }
    };
    focusInitial();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onEscape?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const tabbables = getTabbable(container);
      if (tabbables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !container.contains(activeEl)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Restore focus to the element that had it before the trap activated.
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef, onEscape, initialFocusRef]);
}

export default useFocusTrap;
